<template>
  <v-col :cols="12">
    <div v-if="['string', 'numeric', 'email'].includes(inputType)">
      <v-sheet
        v-if="multi && inputType !== 'select'"
        elevation="1"
        style="padding: 6px; margin-bottom: 10px"
      >
        <div style="margin-bottom: 10px">
          <div
            style="font-size: 16px; font-weight: 400;"
          >
            {{ title }}
          </div>
          <div>
            {{ description }}
          </div>
        </div>
        <v-row
          v-for="(v, idx) in value"
          :key="key+'_'+idx"
          dense
        >
          <v-col :cols="12">
            <v-text-field
              :key="key+'_'+idx"
              :value="v"
              :type="inputType"
              dense
              :placeholder="$t('enter_email')"
              :error-messages="errors[key]"
              :messages="description"
              @input="val => updateMultiVal(val, idx)"
            />
          </v-col>
        </v-row>
        <v-row
          :key="key+'_'+value.length"
          dense
        >
          <v-col :cols="12">
            <v-text-field
              :key="key+'_'+value.length"
              :value="''"
              :type="inputType"
              dense
              :placeholder="$t('enter_email')"
              :error-messages="errors[key]"
              :messages="description"
              @input="val => updateMultiVal(val, null)"
            />
          </v-col>
        </v-row>
      </v-sheet>
      <v-text-field
        v-else
        :value="value"
        :label="title"
        :type="inputType"
        dense
        :error-messages="errors[key]"
        :messages="description"
        @input="val => $emit('update:value', {val, key})"
      />
    </div>

    <div v-if="inputType === 'cron'">
      <div
        class="v-label v-label--active theme--light"
        style="font-size: 11px;"
      >
        {{ title }}
      </div>
      <vue-cron-editor-buefy
        :value="cronValue"
        preserve-state-on-switch-to-advanced
        :locale="$i18n.locale"
        :custom-locales="{
          da: {
            every: 'Hver',
            minutes: 'minut(ter)',
            hoursOnMinute: 'time(r) på minuttet',
            daysAt: 'dag(e) på',
            at: 'på',
            onThe: 'På den',
            dayOfEvery: 'dag, i hver',
            monthsAt: 'månede(r), på',
            everyDay: 'Hver',
            mon: 'Man',
            tue: 'Tirs',
            wed: 'Ons',
            thu: 'Tors',
            fri: 'Fre',
            sat: 'Lør',
            sun: 'Søn',
            hasToBeBetween: 'Skal være imellem',
            and: 'og',
            minutes: 'MINUT',
            hourly: 'TIME',
            daily: 'DAGLIGT',
            weekly: 'UGENTLIGT',
            monthly: 'MÅNEDLIGT',
            advanced: 'AVANCERET',
            cronExpression: 'cron expression:'
          }
        }"
        @input="val => $emit('update:value', {val: {raw: val}, key})"
      />
    </div>
    <v-menu
      v-else-if="inputType === 'time'"
      ref="date-menu"
      v-model="timePicker"
      :close-on-content-click="false"
      max-width="290px"
      offset-y
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          dense
          :value="value"
          :label="title"
          :error-messages="errors[key]"
          :messages="description"
          v-on="on"
        />
      </template>

      <v-time-picker
        scrollable
        :value="value"
        format="24hr"

        @change="val => $emit('update:value', {val, key})"
      />
    </v-menu>

    <v-menu
      v-else-if="inputType === 'date'"
      ref="date-menu"
      v-model="datePicker"
      :close-on-content-click="false"
      max-width="290px"
      offset-y
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          dense
          :value="value"
          :label="title"
          :error-messages="errors[key]"
          :messages="description"
          v-on="on"
        />
      </template>

      <v-date-picker
        :value="value"
        @change="val => $emit('update:value', {val, key})"
      />
    </v-menu>

    <v-select
      v-else-if="inputType === 'select'"
      :value="value"
      :label="title"
      :items="choices"
      :multiple="multi"
      item-text="label"
      item-value="slug"
      :error-messages="errors[key]"
      :messages="description"
      :clearable="!required"
      dense
      @change="val => $emit('update:value', {val, key})"
    />

    <v-checkbox
      v-else-if="inputType === 'boolean'"
      :input-value="value"
      :label="title"
      dense
      class="mr-auto"
      :true-value="true"
      :false-value="false"
      :error-messages="errors[key]"
      :messages="description"
      @change="val => $emit('update:value', {val, key})"
    />

    <v-sheet
      v-else-if="inputType === 'group'"
      elevation="1"
      style="padding: 6px; margin-bottom: 10px"
    >
      <div style="margin-bottom: 10px">
        <div
          style="font-size: 16px; font-weight: 400;"
        >
          {{ title }}
        </div>
        <div>
          {{ description }}
        </div>
      </div>
      <v-row>
        <base-form-builder
          v-for="(subField) in subfields(field)"
          :key="subField.$key"
          :entity="entity"
          :field="subField"
          :errors="errors"
          @update:value="val => $emit('update:value',val)"
        />
      </v-row>
    </v-sheet>
  </v-col>
</template>

<script>
  import VueCronEditorBuefy from 'vue-cron-editor-buefy'

  export default {
    name: 'BoardingInputRender',
    components: { VueCronEditorBuefy },
    props: {
      field: {
        type: Object,
        default: () => ({}),
      },
      entity: {
        type: Object,
        default: () => ({}),
      },
      errors: {
        type: Object,
        default: () => ({}),
      },
    },
    data: () => ({
      datePicker: false,
      timePicker: false,
    }),
    computed: {
      key: function () {
        return this.field.$key || null
      },
      title: function () {
        let title = this.field.$title || null
        if (this.required) { title += ' *' }

        return title
      },
      required: function () {
        return this.field.required
      },
      multi: function () {
        return this.field.multi
      },
      description: function () {
        return this.field.$description || null
      },
      inputType: function () {
        return this.field.type || this.field.$type
      },
      value: function () {
        let val = null
        if (this.key) { val = this.$_.get(this.entity, this.key) }
        if (this.multi && val === null) { return [] }
        if (this.inputType === 'boolean') { return !!val }
        return val
      },
      cronValue: function () {
        if (this.inputType === 'cron') { return this.value?.raw || null }
        return null
      },
      choices: function () {
        if (!this.field?.choices) { return [] }
        let choices = {}

        if (!this.$_.isObject(this.field.choices)) { this.field.choices.each((c, index) => { choices[index] = c }) } else { choices = this.field.choices }

        return Object.keys(choices).map(key => ({ slug: key, label: choices[key] }))
      },
    },
    methods: {
      subfields: function (field) {
        return field.inputs
      },
      updateMultiVal: function (val, index) {
        console.log(val, index)
        const vals = [...this.value]

        if (index == null) { vals.push(val) } else {
          if (val) { vals.splice(index, 1, val) } else vals.splice(index, 1)
        }
        this.$emit('update:value', { val: vals, key: this.key })
      },
    },
  }
</script>
